import { SelectProps } from "../../../form-types";
import { cityList } from "@lab/src/lib/constants";
import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "../../../../lib/utils";
import { Button } from "../../../../components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
} from "../../../../components/ui/command";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "../../../../components/ui/popover";
import { InputWrapper } from "@/dentlab/src/components/input-wrapper";

interface CitySelectProps extends Omit<SelectProps, "onChange"> {
	onChange: (value: { postalCode: string; city: string }) => void;
	className?: string;
	triggerClassName?: string;
}

const ITEM_HEIGHT = 36; // Height of each item in pixels
const LIST_HEIGHT = 300; // Maximum height of the list
const VISIBLE_ITEMS = Math.ceil(LIST_HEIGHT / ITEM_HEIGHT);

export const CitySelectShadcn: React.FC<CitySelectProps> = (props) => {
	const {
		onChange,
		value: parentValue,
		className,
		triggerClassName,
		inputHeight,
	} = props;
	const [open, setOpen] = React.useState(false);
	const [value, setValue] = React.useState(parentValue ?? "");
	const [searchQuery, setSearchQuery] = React.useState("");
	const [scrollTop, setScrollTop] = React.useState(0);
	const [prevValue, setPrevValue] = React.useState(parentValue ?? "");
	const listRef = React.useRef<HTMLDivElement>(null);

	if (prevValue !== parentValue) {
		setValue(parentValue ?? "");
		setPrevValue(parentValue ?? "");
	}

	const filteredCities = React.useMemo(() => {
		return cityList.filter((city) =>
			city.toLowerCase().includes(searchQuery.toLowerCase())
		);
	}, [searchQuery]);

	const handleSelect = (currentValue: string) => {
		setValue(currentValue);
		setOpen(false);

		if (currentValue) {
			const postalCode = currentValue.split(" ")[0];
			const city = currentValue.split(" ").slice(1).join(" ");
			onChange({ postalCode, city });
		} else {
			onChange({ postalCode: "", city: "" });
		}
	};

	const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
		setScrollTop(event.currentTarget.scrollTop);
	};

	const startIndex = Math.floor(scrollTop / ITEM_HEIGHT);
	const endIndex = Math.min(
		startIndex + VISIBLE_ITEMS + 1,
		filteredCities.length
	);

	const visibleCities = filteredCities.slice(startIndex, endIndex);
	const totalHeight = filteredCities.length * ITEM_HEIGHT;

	return (
		<InputWrapper label="PLZ/ORT" required={props.required}>
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger asChild>
					<Button
						variant="outline"
						role="combobox"
						aria-expanded={open}
						className={cn(
							"w-full justify-between font-normal truncate",
							triggerClassName || "h-12",
							className
						)}
						style={
							inputHeight
								? { height: `${inputHeight}px` }
								: undefined
						}
					>
						{value || "PLZ/ORT"}
						<ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
					</Button>
				</PopoverTrigger>
				<PopoverContent className="w-full p-0">
					<Command data-testid="city-select-input">
						<CommandInput
							placeholder="Suche PLZ/ORT..."
							value={searchQuery}
							onValueChange={setSearchQuery}
						/>
						<CommandEmpty>Keine Ergebnisse gefunden.</CommandEmpty>
						<CommandGroup>
							<div
								ref={listRef}
								style={{
									height: Math.min(LIST_HEIGHT, totalHeight),
									overflow: "auto",
									position: "relative",
								}}
								onScroll={handleScroll}
							>
								<div
									style={{
										height: totalHeight,
										position: "relative",
									}}
								>
									{visibleCities.map((city, index) => (
										<CommandItem
											key={city}
											value={city}
											onSelect={handleSelect}
											style={{
												position: "absolute",
												top:
													(startIndex + index) *
													ITEM_HEIGHT,
												left: 0,
												right: 0,
												height: ITEM_HEIGHT,
											}}
											data-testid="city-select-item"
										>
											{value === city && (
												<Check
													className={cn("h-4 w-4")}
												/>
											)}
											{city}
										</CommandItem>
									))}
								</div>
							</div>
						</CommandGroup>
					</Command>
				</PopoverContent>
			</Popover>
		</InputWrapper>
	);
};
